import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

jest.mock("react-router-dom", () => ({
  useParams: () => ({ workspace_id: "ws-1" }),
  useNavigate: () => jest.fn(),
  useSearchParams: () => [new URLSearchParams(""), jest.fn()],
}));

// Stable identity, like the real provider's: the results effect depends on it.
const mockWorkspace = { id: "ws-1" };
jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({
    workspace: mockWorkspace,
    isAdmin: false,
    isEditor: false,
  }),
}));

jest.mock("../../../providers/FeatureFlagProvider", () => ({
  useFeatureFlag: () => false,
}));

const mockTutorialRef = () => {};
const mockStartTour = jest.fn();
jest.mock("../../../components/Tutorial", () => ({
  useTutorialTarget: () => mockTutorialRef,
  useTutorial: () => ({ startTour: mockStartTour }),
}));

jest.mock("../tutorial", () => ({ buildBkgCompaniesTour: () => ({}) }));

// The filter rail shows from lg up; setupTests' matchMedia answers false.
jest.mock("../hooks/useWideLayout", () => ({ useWideLayout: () => true }));

const mockSearch = jest.fn();
const mockCompanyFilter = jest.fn();
jest.mock("../hooks", () => ({
  useBKGSearch: () => mockSearch,
  useBKGIndexCompanyFilter: () => mockCompanyFilter,
  useBKGCompanyExport: () => jest.fn(),
  useBKGCompanyPersonsBulk: () => jest.fn(),
  useBKGIngest: () => jest.fn(),
}));

// A stand-in FilterUI: one button that commits whatever values the test sets.
let mockFilterValues: any = {};
jest.mock("./SearchFilters", () => ({
  FilterUI: ({ onChange }: any) => (
    <button onClick={() => onChange(mockFilterValues)}>apply filter</button>
  ),
}));

jest.mock("../../../components/Loading", () => () => (
  <div data-testid="initial-loading" />
));
jest.mock("../components/BKGAgent", () => ({ FloatingChatWidget: () => null }));
jest.mock("../components/ExportDropdown", () => () => null);
jest.mock("../components/Modals/AddCompany/AddCompanyModal", () => () => null);
jest.mock("../components/Modals/AddEvent/AddEventModal", () => () => null);
jest.mock("../components/Modals/ViewQueue/ViewQueueModal", () => () => null);

// eslint-disable-next-line import/first
import Search from "./Search";

const deferred = <T,>() => {
  let resolve!: (value: T) => void;
  const promise = new Promise<T>((r) => {
    resolve = r;
  });
  return { promise, resolve };
};

const page = (...names: string[]) => ({
  results: names.map((name) => ({
    _id: `ws-1/company/${name}`,
    value: { name: { value: name } },
  })),
  total: names.length,
});

const NAME = "value.name.value";
const schema = {
  path: "value",
  type: "object",
  children: [{ path: NAME, type: "string", allowed_values: ["Acme"] }],
};
const nameIs = (name: string) => ({
  [NAME]: { path: NAME, type: "string", allowed_values: [name] },
});

const newClient = () =>
  new QueryClient({ defaultOptions: { queries: { retry: false } } });

const withClient = (client: QueryClient) => (
  <QueryClientProvider client={client}>
    <Search />
  </QueryClientProvider>
);

const renderSearch = () => render(withClient(newClient()));

// The rail starts collapsed, so open it before reaching the filters.
const findApplyFilter = async () => {
  fireEvent.click(await screen.findByRole("button", { name: /^Filters/ }));
  return screen.findByRole("button", { name: "apply filter" });
};

describe("Search data loading", () => {
  beforeEach(() => {
    jest.clearAllMocks();
    mockFilterValues = {};
  });

  it("requests results without waiting for the filter schema", async () => {
    mockCompanyFilter.mockReturnValue(new Promise(() => {}));
    mockSearch.mockResolvedValue(page("Acme"));

    renderSearch();

    expect(await screen.findByText("Acme")).toBeInTheDocument();
    // The third argument is React Query's abort signal: the search is
    // cancellable, so a superseded one stops instead of finishing.
    expect(mockSearch).toHaveBeenCalledWith(
      "ws-1",
      {
        skip: 0,
        limit: 50,
        company_filter: {},
      },
      expect.any(AbortSignal),
    );
  });

  it("keeps the current results on screen while a refetch is in flight", async () => {
    mockCompanyFilter.mockResolvedValue(schema);
    mockSearch.mockResolvedValueOnce(page("Acme"));

    renderSearch();
    expect(await screen.findByText("Acme")).toBeInTheDocument();

    const refetch = deferred<ReturnType<typeof page>>();
    mockSearch.mockReturnValueOnce(refetch.promise);
    mockFilterValues = nameIs("Zeta");
    fireEvent.click(await findApplyFilter());

    expect(
      screen.getByRole("progressbar", { name: "Updating results" }),
    ).toBeInTheDocument();
    expect(screen.getByText("Acme")).toBeInTheDocument();
    expect(screen.queryByTestId("initial-loading")).not.toBeInTheDocument();

    await act(async () => refetch.resolve(page("Biogen")));

    expect(await screen.findByText("Biogen")).toBeInTheDocument();
    expect(screen.queryByText("Acme")).not.toBeInTheDocument();
    expect(
      screen.queryByRole("progressbar", { name: "Updating results" }),
    ).not.toBeInTheDocument();
  });

  it("ignores a response that arrives after a newer one", async () => {
    mockCompanyFilter.mockResolvedValue(schema);
    const first = deferred<ReturnType<typeof page>>();
    const second = deferred<ReturnType<typeof page>>();
    mockSearch
      .mockReturnValueOnce(first.promise)
      .mockReturnValueOnce(second.promise);

    renderSearch();
    const applyFilter = await findApplyFilter();
    mockFilterValues = nameIs("Zeta");
    fireEvent.click(applyFilter);
    await waitFor(() => expect(mockSearch).toHaveBeenCalledTimes(2));

    await act(async () => second.resolve(page("Newer")));
    expect(await screen.findByText("Newer")).toBeInTheDocument();

    await act(async () => first.resolve(page("Stale")));
    expect(screen.queryByText("Stale")).not.toBeInTheDocument();
    expect(screen.getByText("Newer")).toBeInTheDocument();
  });

  it("shows cached results at once when the view mounts again", async () => {
    mockCompanyFilter.mockResolvedValue(schema);
    mockSearch.mockResolvedValue(page("Acme"));
    const client = newClient();

    const { unmount } = render(withClient(client));
    expect(await screen.findByText("Acme")).toBeInTheDocument();
    unmount();

    // e.g. back from a company profile: no spinner, the page is there at once.
    render(withClient(client));
    expect(screen.getByText("Acme")).toBeInTheDocument();
    expect(screen.queryByTestId("initial-loading")).not.toBeInTheDocument();
  });

  it("clears every filter from the chip row and shows unfiltered results", async () => {
    mockCompanyFilter.mockResolvedValue(schema);
    mockSearch
      .mockResolvedValue(page("Acme"))
      .mockResolvedValueOnce(page("Acme"))
      .mockResolvedValueOnce(page("Filtered"));

    renderSearch();
    expect(await screen.findByText("Acme")).toBeInTheDocument();

    mockFilterValues = nameIs("Zeta");
    fireEvent.click(await findApplyFilter());
    expect(await screen.findByText("Filtered")).toBeInTheDocument();
    expect(
      screen.getByRole("button", { name: "Remove Name: Zeta" }),
    ).toBeInTheDocument();

    fireEvent.click(screen.getByRole("button", { name: "Clear all" }));

    expect(await screen.findByText("Acme")).toBeInTheDocument();
    expect(
      screen.queryByRole("button", { name: "Clear all" }),
    ).not.toBeInTheDocument();
    expect(mockSearch).toHaveBeenLastCalledWith(
      "ws-1",
      {
        skip: 0,
        limit: 50,
        company_filter: {},
      },
      expect.any(AbortSignal),
    );
  });
});
